import { ComputedRef, Ref, computed, ref, watch } from 'vue';
import { ConsolidatedSKU, fetchConsolidatedSKUsRaw, updateShopifySKUs, UpdatesNeededByParentCode } from './mintechAPI';

export const useInventoryManager = () => {
	const listModule = createListModule();
	const searchFilterModule = createSearchModule(listModule);
	const paginationModule = createPaginationModule(searchFilterModule.searchedUpdateNeededSKUs);
	const tableModule = createTableModule(paginationModule.pagedUpdateNeededSKUs);
	const dataAccessModule = createDataAccessModule(listModule);
	const updateModule = createUpdateModule(listModule);

	return {
		...listModule,
		...searchFilterModule,
		...paginationModule,
		...tableModule,
		...dataAccessModule,
		...updateModule
	};
};

//#region List
function createListModule() {
	// -- From API --
	// all consolidated SKUs fetched from the API
	const consolidatedSKUs: Ref<ConsolidatedSKU[]> = ref([]);
	// maps parent codes to the list of SKUs that need updates
	const updatesNeededByParentCode = ref<Map<string, ConsolidatedSKU[]>>(new Map());

	// -- UI State --
	// set of selected parent codes
	const selectedSKUs: Ref<Set<string>> = ref(new Set());

	// -- Computed --
	// array of UpdatesNeededByParentCode map for easier iteration
	const updatesNeededByParentCodeArray: ComputedRef<UpdatesNeededByParentCode[]> = computed(() =>
		Array.from(updatesNeededByParentCode.value, ([parentCode, consolSKUs]) => ({ parentCode, consolSKUs }))
	);
	// flat list of all SKUs that need updates based on current selection;
	// if no selection, includes all SKUs that need updates
	const updatesNeededConsolSKUs: ComputedRef<ConsolidatedSKU[]> = computed(() => {
		const matches: ConsolidatedSKU[] = [];

		if (selectedSKUs.value.size === 0) {
			for (const consolSKUs of updatesNeededByParentCode.value.values()) {
				if (!consolSKUs || consolSKUs.length === 0) {
					continue;
				}

				matches.push(...consolSKUs);
			}

			return matches;
		}

		for (const parentCode of selectedSKUs.value) {
			const matchingConsolSKU = updatesNeededByParentCode.value.get(parentCode);
			if (!matchingConsolSKU || matchingConsolSKU.length === 0) {
				continue;
			}

			matches.push(...matchingConsolSKU);
		}

		return matches;
	});

	return {
		consolidatedSKUs,
		updatesNeededByParentCode,
		updatesNeededByParentCodeArray,
		selectedSKUs,
		updatesNeededConsolSKUs
	};
}
//#endregion

//#region Search Filter
function createSearchModule(skuLists: ReturnType<typeof createListModule>) {
	const searchTerm = ref<string>('');

	const searchedUpdateNeededSKUs: ComputedRef<UpdatesNeededByParentCode[]> = computed(() => {
		if (!searchTerm.value.trim()) return skuLists.updatesNeededByParentCodeArray.value;
		const lowerSearchTerm = searchTerm.value.toLowerCase();
		return skuLists.updatesNeededByParentCodeArray.value.filter((updateNeeded) => {
			const parentCode = updateNeeded.parentCode.toLowerCase();
			return parentCode.includes(lowerSearchTerm);
		});
	});

	return { searchTerm, searchedUpdateNeededSKUs };
}
//#endregion

//#region Pagination
function createPaginationModule(filteredSKUs: ComputedRef<UpdatesNeededByParentCode[]>) {
	const pageSize = ref<number>(15);
	const currentPage = ref<number>(1);
	const totalItems = computed<number>(() => filteredSKUs.value.length);
	const totalPages = computed<number>(() => Math.max(1, Math.ceil(totalItems.value / pageSize.value)));
	const pageStartIndex = computed(() => (currentPage.value - 1) * pageSize.value);
	const pageEndIndex = computed(() => Math.min(pageStartIndex.value + pageSize.value, totalItems.value));
	const pagedUpdateNeededSKUs = computed(() => filteredSKUs.value.slice(pageStartIndex.value, pageEndIndex.value));
	const isFirstPage = computed(() => currentPage.value === 1);
	const isLastPage = computed(() => currentPage.value === totalPages.value);

	function nextPage() {
		if (currentPage.value < totalPages.value) {
			currentPage.value++;
		}
	}

	function prevPage() {
		if (currentPage.value > 1) {
			currentPage.value--;
		}
	}

	function firstPage() {
		currentPage.value = 1;
	}

	function lastPage() {
		currentPage.value = totalPages.value;
	}

	watch(totalPages, (newVal) => {
		if (currentPage.value > newVal) {
			currentPage.value = newVal;
		}
	});

	return {
		currentPage,
		totalItems,
		totalPages,
		pageStartIndex,
		pageEndIndex,
		pagedUpdateNeededSKUs,
		isFirstPage,
		isLastPage,
		nextPage,
		prevPage,
		firstPage,
		lastPage
	};
}

//#endregion

//#region Table
function createTableModule(pagedUpdateNeededSKUs: ComputedRef<UpdatesNeededByParentCode[]>) {
	type TableRow = {
		parentCode: string;
		skus: string;
		effects: string;
		acumaticaValues: string;
		shopifyValues: string;
	};

	const rowData: ComputedRef<TableRow[]> = computed(() => {
		let rows: TableRow[] = [];
		for (const update of pagedUpdateNeededSKUs.value) {
			let parentCode = update.parentCode;
			let combinedSKUs = '';
			let combinedEffects = '';
			let combinedAcumaticaValues = '';
			let combinedShopifyValues = '';

			for (const consolSKU of update.consolSKUs) {
				// Check for dual updates
				if (consolSKU.priceUpdateNeeded && consolSKU.qtyUpdateNeeded) {
					combinedSKUs += `\n${consolSKU.sku}\n${consolSKU.sku}`;
					combinedEffects += `\nPrice\nQuantity`;
					combinedAcumaticaValues += `\n${consolSKU.acumaticaPrice}\n${consolSKU.acumaticaQty}`;
					combinedShopifyValues += `\n${consolSKU.shopifyPrice}\n${consolSKU.shopifyQty}`;
				} else {
					combinedSKUs += `\n${consolSKU.sku}`;
					combinedEffects += `\n${consolSKU.priceUpdateNeeded ? 'Price' : 'Quantity'}`;
					combinedAcumaticaValues += `\n${consolSKU.priceUpdateNeeded ? consolSKU.acumaticaPrice : consolSKU.acumaticaQty}`;
					combinedShopifyValues += `\n${consolSKU.priceUpdateNeeded ? consolSKU.shopifyPrice : consolSKU.shopifyQty}`;
				}
			}

			const tableRow: TableRow = {
				parentCode,
				skus: combinedSKUs.trim(),
				effects: combinedEffects.trim(),
				acumaticaValues: combinedAcumaticaValues.trim(),
				shopifyValues: combinedShopifyValues.trim()
			};

			rows.push(tableRow);
		}
		return rows;
	});

	return { rowData };
}
//#endregion

//#region Fetch
function createDataAccessModule(skuLists: ReturnType<typeof createListModule>) {
	async function fetchConsolidatedSKUs() {
		skuLists.consolidatedSKUs.value = [];
		skuLists.updatesNeededByParentCode.value = new Map();
		try {
			const response = await fetchConsolidatedSKUsRaw();
			const updatesMap = new Map<string, ConsolidatedSKU[]>();
			for (const update of response.updatesNeededByParentCode) {
				updatesMap.set(update.parentCode, update.consolSKUs);
			}

			skuLists.consolidatedSKUs.value = response.consolidateSKUs;
			skuLists.updatesNeededByParentCode.value = updatesMap;
		} catch (e: any) {
			skuLists.consolidatedSKUs.value = [];
			skuLists.updatesNeededByParentCode.value = new Map();
		}
	}

	return {
		fetchConsolidatedSKUs
	};
}
//#endregion

//#region Update
export type UpdateType = 'all' | 'price' | 'quantity';
type UpdateTypeFlags = Record<UpdateType, boolean>;

function createUpdateModule(skuLists: ReturnType<typeof createListModule>) {
	const currentlyUpdatingSKU: Ref<string | null> = ref(null);

	const isUpdateNecessary: ComputedRef<UpdateTypeFlags> = computed(() => ({
		all: skuLists.updatesNeededConsolSKUs.value.length > 0,
		price: skuLists.updatesNeededConsolSKUs.value.some((sku) => sku.priceUpdateNeeded === true),
		quantity: skuLists.updatesNeededConsolSKUs.value.some((sku) => sku.qtyUpdateNeeded === true)
	}));

	const isUpdateValid: ComputedRef<UpdateTypeFlags> = computed(() => ({
		all: !skuLists.updatesNeededConsolSKUs.value.some(
			(sku) => sku.priceUpdateNeeded === true && sku.acumaticaPrice === 0 && (sku.acumaticaQty ?? 0) > 0
		),
		quantity: true,
		price: !skuLists.updatesNeededConsolSKUs.value.some(
			(sku) => sku.priceUpdateNeeded === true && sku.acumaticaPrice === 0 && (sku.acumaticaQty ?? 0) > 0
		)
	}));

	async function triggerUpdate(updateType: UpdateType) {
		for (const sku of skuLists.updatesNeededConsolSKUs.value) {
			const shouldSendUpdate =
				updateType === 'all'
					? sku.priceUpdateNeeded === true || sku.qtyUpdateNeeded === true
					: updateType === 'price'
					? sku.priceUpdateNeeded === true
					: sku.qtyUpdateNeeded === true;

			if (!shouldSendUpdate) {
				continue;
			}

			currentlyUpdatingSKU.value = sku.sku;
			await updateShopifySKUs(updateType, sku);
		}
	}

	return { currentlyUpdatingSKU, isUpdateNecessary, isUpdateValid, triggerUpdate };
}

//#endregion
